import React from 'react';
import { Field, reduxForm, formPropTypes, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import exact from 'prop-types-exact';
import PropTypes from 'prop-types';

import {
  Modal,
  FormGroup,
  Row,
  Col,
  Button,
  ButtonToolbar,
  FormControl,
  Checkbox,
} from 'react-bootstrap';

import UserPicker from './UserPickerEnhanced';
import { createRenderer } from '../../features/reduxForm/utils';

const FORM_NAME = 'rejectReasonForm';
const selector = formValueSelector('rejectReasonForm');

const propTypes = exact({
  show: PropTypes.bool.isRequired,
  submittedBy: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loggedInUser: PropTypes.object.isRequired,

  validate: PropTypes.func.isRequired,
  reason: PropTypes.string,
  notifySubmitter: PropTypes.bool.isRequired,
  usersToNotify: PropTypes.array,
  ...formPropTypes,
});

const validate = (values) => {
  const errors = [];

  if (!values.reason && values.notifySubmitter) {
    errors.reason = 'Enter in a reason';
  }

  return errors;
};

const RenderInput = createRenderer((input, label, { disabled }) => {
  return (
    <FormControl type="input" {...input} disabled={disabled} />
  );
});

const RenderUserPicker = createRenderer((input) => {
  return (
    <UserPicker {...input} />
  );
});

const RenderCheckBox = createRenderer((input, label, { disabled }) => {
  return (
    <Checkbox checked={input.value} disabled={disabled} {...input} />
  );
});

const RejectReasonForm = (props) => {
  const { handleSubmit, submitting, invalid, pristine, reset } = props;
  const { reason, notifySubmitter, usersToNotify, onConfirm } = props;
  const { loggedInUser, submittedBy } = props;
  const { show, onCancel, error } = props;

  const onSubmit = () => {
    let userIds = [];
    if (notifySubmitter && usersToNotify) {
      userIds = usersToNotify.map((user) => {
        return user.id;
      });
    }

    onConfirm(reason, notifySubmitter, userIds);
    reset();
  };

  const handleOnCancel = () => {
    reset();
    onCancel();
  };

  const renderMessage = () => {
    const loggedInUserFullname = `${loggedInUser.firstname} ${loggedInUser.lastname}`;

    if (!notifySubmitter) {
      return (
        <p>{submittedBy} will not be notified</p>
      );
    }

    return (
      <div>
        The following message will be sent to {submittedBy}: <br />
        {loggedInUserFullname} rejected the attached image
        because `{reason}` <br /> <br />
        <div>
          {usersToNotify && usersToNotify.map((user) => {
            return (
              <span key={user.id}>{user.fullname} will also be notified<br /> </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleOnCancel}>
      <Modal.Header closeButton>
        <Modal.Title>Reject Submission</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col sm={6}>
              <FormGroup>
                <Field
                  name="reason"
                  label="Reject Reason"
                  type="text"
                  component={RenderInput}
                />
              </FormGroup>
              <Field
                name="notifySubmitter"
                label="Notify Submitter"
                type="checkbox"
                component={RenderCheckBox}
              />
              {notifySubmitter
                && (
                <FormGroup>
                  <Field
                    name="usersToNotify"
                    label="Also notify"
                    format={null}
                    type="select"
                    component={RenderUserPicker}
                  />
                </FormGroup>
                )}
            </Col>
            <Col sm={6}>
              {renderMessage()}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <ButtonToolbar className="pull-right">
                <Button bsStyle="warning" onClick={handleOnCancel}>Cancel</Button>
                <Button
                  bsStyle="primary"
                  type="submit"
                  disabled={(submitting || pristine || invalid)}
                >
                  Submit
                </Button>
              </ButtonToolbar>
            </Col>
          </Row>
          <label>{error && <strong>{error}</strong>}</label>
        </form>
      </Modal.Body>
    </Modal>
  );
};

RejectReasonForm.propTypes = propTypes;

const withForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
  initialValues: {
    notifySubmitter: true,
  },
});

const withRedux = connect((state) => {
  const reason = selector(state, 'reason');
  const notifySubmitter = selector(state, 'notifySubmitter');
  const usersToNotify = selector(state, 'usersToNotify');

  return {
    reason,
    notifySubmitter,
    usersToNotify,
  };
}, {});

const enhance = compose(
  withForm,
  withRedux,
);

export default enhance(RejectReasonForm);
